import type { GetUploadLink } from '../plugins/AzureBlobUploaderPlugin';
import type {
  FileUploadRestrictions,
  OnAfterUploadFn,
  UploaderFile,
  UploaderMeta,
  UploadSuccessResult,
} from '../types';
import type { UppyLocale } from './getUppyLocale';
import type { Meta, UploadResult } from '@uppy/core';

import { useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormatRestrictionError } from './useFormatRestrictionError';
import { useUploadConfirmation } from './useUploadConfirmation';
import { useUploaderEvents } from './useUploaderEvents';
import { useUploaderInstance } from './useUploaderInstance';

type AnyUploadResult = UploadResult<Meta, Record<string, never>>;

/** Props that configure the upload itself, independent of how it is presented. */
export type UploaderControllerProps<TMeta extends UploaderMeta = UploaderMeta> =
  {
    autoProceed?: boolean;
    getUploadLink: GetUploadLink;
    meta?: TMeta;
    onAfterUpload?: OnAfterUploadFn<TMeta>;
    onError?: (fileName: string, error: Error) => void;
    onFileAdded?: (file: UploaderFile<TMeta>) => void;
    onStartUpload?: (uploadId: string, files: UploaderFile<TMeta>[]) => void;
    onSuccess?: (result: UploadSuccessResult<TMeta>) => Promise<void> | void;
    onUploadComplete?: (result: AnyUploadResult) => void;
    onUploadProgress?: (
      file: UploaderFile<TMeta> | undefined,
      progress: { bytesUploaded: number; bytesTotal: number | null },
    ) => void;
    restrictions?: FileUploadRestrictions;
    uppyLocaleOverrides?: Partial<UppyLocale['strings']>;
  };

/**
 * The upload logic shared by `Uploader` and `UploaderInline`, independent of
 * how it is presented: creates the uploading Uppy instance, forwards its
 * events to the consumer callbacks, and owns the restriction and
 * confirmation errors shown above the dropzone.
 *
 * @param onClose - Called after a confirmed upload and from `handleClose`, so
 *   a hosting surface (e.g. a modal) can dismiss itself.
 * @returns The Uppy instance and its `validatedRestrictions`; the current
 *   `restrictionError` and `confirmationError`, and the `errorMessage` to show
 *   of the two (a restriction error wins); `isConfirming`;
 *   `confirmUpload`, which runs `onSuccess` and then empties the queue;
 *   `reportError`, which shows an upload error inline; and `handleClose`,
 *   which cancels uploads, discards the queue and resets both errors.
 */
export function useUploaderController<TMeta extends UploaderMeta>(
  props: UploaderControllerProps<TMeta>,
  onClose?: () => void,
) {
  const { i18n } = useTranslation('ui');

  const id = useId();

  const [restrictionError, setRestrictionError] = useState<string | null>(null);

  const { uppy, validatedRestrictions } = useUploaderInstance({
    autoProceed: props.autoProceed ?? false,
    getUploadLink: props.getUploadLink,
    id,
    language: i18n.language,
    meta: props.meta,
    onAfterUpload: props.onAfterUpload,
    restrictions: props.restrictions,
    uppyLocaleOverrides: props.uppyLocaleOverrides,
  });
  const {
    clearError: clearConfirmationError,
    confirmUpload,
    error: confirmationError,
    isConfirming,
    reportError,
  } = useUploadConfirmation({
    onClose,
    onSuccess: props.onSuccess,
    uppy,
  });
  const formatRestrictionError = useFormatRestrictionError();

  useUploaderEvents({
    clearConfirmationError,
    formatRestrictionError,
    onError: props.onError,
    onFileAdded: props.onFileAdded,
    onStartUpload: props.onStartUpload,
    onUploadComplete: props.onUploadComplete,
    onUploadProgress: props.onUploadProgress,
    setRestrictionError,
    uppy,
    validatedRestrictions,
  });

  const handleClose = useCallback(() => {
    uppy.cancelAll();
    uppy.removeFiles(uppy.getFiles().map((file) => file.id)); // Remove all queued or completed uploads as well

    clearConfirmationError();
    setRestrictionError(null);

    onClose?.();
  }, [clearConfirmationError, onClose, uppy]);

  return {
    confirmationError,
    confirmUpload,
    errorMessage: restrictionError ?? confirmationError,
    handleClose,
    isConfirming,
    reportError,
    restrictionError,
    uppy,
    validatedRestrictions,
  };
}
